<script setup>
import { computed } from 'vue';
import { useAutocompletions, useUpdateQuery } from './composables';
import { queryTypes } from './constants';
import { useStore } from 'vuex';
import { paramIconMap } from './constants';
import SvgIcon from 'JIX/components/SvgIcon.vue';
import { __, __x } from 'JIX/gettext';
import RenderTranslation from '../RenderTranslation.vue';
import 'icons/button-check.svg';


const props = defineProps({
    selectedIndex: {
        type: [Number, null],
        default: null,
    },
    name: {
        type: String,
        required: true,
    },
    completionTypes: {
        type: Array,
        default: () => queryTypes,
    },
});

const userInput = defineModel('userInput', {
    type: String,
    default: '',
});

const show = defineModel('show', {
    type: Boolean,
    default: true,
});

const store = useStore();
defineEmits(['click']);

const filteredCompletionTypes = store.state.eligibleForNewJobsearchJobtitles ? props.completionTypes : props.completionTypes.filter(type => type !== 'jobtitleid');

const {
    requestCompletionsUpdate,
    filteredCompletions: _filteredCompletions,
    orderedSuggestions,
    searching,
} = useAutocompletions(userInput, filteredCompletionTypes);

const {
    handleSelectSuggestion,
    handleRemoveChosenQueryParam,
} = useUpdateQuery(requestCompletionsUpdate, store);

function handleSelectSuggestionWrapper(suggestion, metaData) {
    if (isInSearchQuery(metaData.type, suggestion)) {
        handleRemoveChosenQueryParam(suggestion, metaData.type);
    } else {
        handleSelectSuggestion(suggestion, metaData);
    }
    userInput.value = '';
    return {
        emitEvent: {
            name: 'suggestionSelected',
            params: {},
        },
    };
}

const numSuggestions = computed(() => orderedSuggestions.value.flatMap(([_, { completions } ]) => completions).length);

function getFlatIndex(groupIndex, index) {
    return orderedSuggestions.value.slice(0, groupIndex).flatMap(([_, { completions } ]) => completions).length + index;
}

const clickHandlers = computed(() => {
    return orderedSuggestions.value.flatMap(([suggestionType, completionResult]) => {
        const metaData = {
            type: suggestionType,
            title: completionResult.title,
        };
        return completionResult.completions.map(suggestion => () => handleSelectSuggestionWrapper(suggestion, metaData));
    });
});


defineExpose({
    numSelectableItems: numSuggestions,
    clickHandlers,
    name: props.name,
    show,
    hasContent: computed(() => userInput.value.length),
});

function isInSearchQuery(type, suggestion) {
    return store.getters.workingQuery[type]?.includes(suggestion.value);
}

</script>


<template>
    <div v-show="show" :class="{ 'jobsearch-dropdown-container': show }">
        <template v-if="numSuggestions">
            <template v-for="[suggestionType, { title, completions: suggestions } ], groupIndex in orderedSuggestions" :key="suggestionType">
                <div class="jobsearch-dropdown-section" v-if="suggestions.length">
                    <div class="category-header jobsearch-dropdown-section__header">
                        {{ title }}
                    </div>
                    <ul @mousedown.prevent>
                        <li
                            v-for="suggestion, index in suggestions"
                            @click="$emit('click', getFlatIndex(groupIndex, index))"
                            class="jobsearch-dropdown-section__suggestion d-flex justify-content-between"
                            :class="{ 'jobsearch-dropdown-section__suggestion--selected': selectedIndex === getFlatIndex(groupIndex, index) }"
                            :key="index"
                        >
                            <div>
                                <SvgIcon
                                    :name="paramIconMap[suggestionType]"
                                    size="xs"
                                    role="presentation"
                                    class="mr-2"
                                    v-if="paramIconMap[suggestionType]"
                                />
                                <span class="font-weight-bold" v-if="isInSearchQuery(suggestionType, suggestion)">
                                    {{ suggestion.text }}
                                </span>
                                <span v-else v-html="suggestion.text_hl" />
                            </div>
                            <SvgIcon
                                size="mini"
                                class="search-parameter-selected-icon"
                                name="button-check"
                                v-if="isInSearchQuery(suggestionType, suggestion)"
                            />
                        </li>
                    </ul>
                </div>
            </template>
        </template>
        <div v-else class="jobsearch-dropdown-section px-4">
            <p v-if="!searching">
                <RenderTranslation
                    :message="{
                        translation: __x('Der findes desværre ikke nogen resultater for søgeordet <strong:{userInput}>', {userInput}),
                    }"
                />
            </p>
            <div class="text-center" v-else>
                <div class="mt-3 spinner-border spinner-border-sm" role="status">
                    <span class="sr-only" />
                </div>
            </div>
        </div>
    </div>
</template>
